import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MarkGroup } from '@examdojo/models/markscheme-v2';
import { QuestionEditingOptions } from '@examdojo/models/question-editor';
import { ContentItemFormArray, createContentItemsFormArray } from '@examdojo/question-content-item';

export function arrayMinLength(minLength: number) {
  return (control: AbstractControl): ValidationErrors | null => {
    const invalid = (control as FormArray).controls.length < minLength;
    return invalid ? { arrayMinLength: { minLength, actualLength: control.value.length } } : null;
  };
}

export type MarkGroupFormGroup = FormGroup<{
  id: FormControl<string>;
  topic_ids: FormControl<number[]>;
  mark_code: FormControl<string>;
  marks: FormControl<number>;
  notes: FormArray<FormControl<string>>;
  criterion: ContentItemFormArray;
}>;

export function createMarkGroupNoteFormControl(note: string | undefined = ''): FormControl<string> {
  return new FormControl(note, { nonNullable: true, validators: [Validators.required] });
}

export function createMarkGroupFormGroup(
  markGroup: MarkGroup | null,
  options: QuestionEditingOptions,
): MarkGroupFormGroup {
  const mark_code = fixNullValuesInMarkCode(markGroup?.mark_code);

  const fg = new FormGroup({
    id: new FormControl(markGroup?.id ?? '', { nonNullable: true }),
    topic_ids: new FormControl(markGroup?.topic_ids ?? [], { nonNullable: true }),
    mark_code: new FormControl(mark_code ?? '', { nonNullable: true }),
    marks: new FormControl(markGroup?.marks ?? 0, { nonNullable: true }),
    notes: new FormArray<FormControl<string>>(
      markGroup?.notes.length ? markGroup.notes.map((note) => createMarkGroupNoteFormControl(note)) : [],
      // arrayMinLength(1), ?? how to edit it in image review mode
    ),
    criterion: createContentItemsFormArray(markGroup?.criterion ?? [], {
      allowTextEditing: options.allowMarkschemeEditing,
      allowImageEditing: options.allowImageEditing,
      allowFillInTheBlanks: false,
      hideImageAndTableMetadata: options.hideImageAndTableMetadata,
    }),
  });

  if (!options.allowMarkschemeEditing) {
    fg.get('id')?.disable();
    fg.get('topic_ids')?.disable();
    fg.get('mark_code')?.disable();
    fg.get('marks')?.disable();
    fg.get('notes')?.disable();
  }

  return fg;
}

export function fixNullValuesInMarkCode(mark_code: string | null | undefined) {
  if (mark_code === null || mark_code === undefined) {
    return null;
  }

  const lowerCaseMarkCode = mark_code.toLowerCase();

  const nullValues = ['null', 'na', 'n/a', 'none'];

  if (nullValues.includes(lowerCaseMarkCode)) {
    return null;
  }

  return mark_code;
}
